import {
  defaultStagedThreshold,
  defaultTestInterval,
  type IEventCancellation,
  lapsedMilliseconds
} from './staged-event.js';
import { getApiToken, getCurrentUser } from '../api/current-user.js';
import { globalState } from '../ui/global-state.js';
import { debounce } from '../common/helpers/callbacks';

export type EventSourceHandler = (data: any, eventName: string) => void;
export interface IStagedExecutionOptions {
  event: () => void | Promise<void>;
  threshold?: number;
  title?: string;
  eventTriggered?: EventSourceHandler;
  eventFinally?: () => void;
  eventActioned?: EventSourceHandler;
  cancelToken?: IEventCancellation;
  testInterval?: number;
}

/**
 * stagedExecution
 *
 * This function creates an event handler that batches incoming events and executes a main action after a specified time threshold.
 * It is useful when you want to avoid running an expensive operation for every event, and instead process them in batches.
 *
 * Usage scenario: Receiving many events in a short time, but only needing to act once after a pause.
 *
 * @param options - IStagedExecutionOptions
 *   event: The main function to execute after the threshold (can be async)
 *   threshold: Time in ms to wait before executing the main event (default: defaultStagedThreshold)
 *   title: Optional name for logging
 *   eventTriggered: Called when the first event is received
 *   eventFinally: Called after the main event finishes
 *   eventActioned: Called for every event received
 *   cancelToken: Optional cancellation support
 *   testInterval: How often to check if threshold is reached (default: defaultTestInterval)
 *
 * Returns: An EventSourceHandler function to be used as an event listener
 */
export function stagedExecution(options: IStagedExecutionOptions): EventSourceHandler {
  // Interval to check if threshold is reached
  const intervalms = options.testInterval ?? defaultTestInterval;
  let timer: NodeJS.Timeout | undefined = undefined;
  let triggered: Date | undefined = undefined; // When first event was received
  let actioned: Date | undefined = undefined; // When last event was received
  let actionCount = 0; // Number of events received
  let eventActionQueue: { data: any; eventName: string }[] = []; // Events received while executing
  let executing = false; // Is the main event running?
  let restart = false; // Should we restart after execution?

  /**
   * Resets the staged execution state and timer
   */
  const reset = () => {
    clearInterval(timer);
    timer = undefined;
    triggered = undefined;
    actionCount = 0;
    actioned = undefined;
  };

  /**
   * Checks if the threshold is reached and runs the main event
   */
  const intervalEvent = async () => {
    // If cancelled, reset and call finally
    if (options.cancelToken?.cancelled) {
      reset();
      options.cancelToken?.reset();
      options.eventFinally?.();
      return;
    }
    // How long since first event?
    const ms = lapsedMilliseconds(triggered!);
    if (ms >= (options.threshold ?? defaultStagedThreshold)) {
      try {
        executing = true;
        await options.event(); // Run main event
        options.eventFinally?.();
      } finally {
        executing = false;
        reset();
        // If new events arrived while executing, process them
        if (restart) {
          try {
            // Call eventTriggered for the first queued event
            options.eventTriggered?.(eventActionQueue[0].data, eventActionQueue[0].eventName);
            // Call eventActioned for all queued events
            eventActionQueue
              .filter((_x, index) => index > 0)
              .forEach(x => {
                options.eventActioned?.(x.data, x.eventName);
              });
          } finally {
            eventActionQueue = [];
          }
          // Start the process again
          startProcess();
        }
      }
      return;
    }
    // Not ready yet, check again after interval
    timer = setTimeout(intervalEvent, intervalms);
  };

  /**
   * The returned event handler function
   * Receives (data, eventName) for each event
   */
  return (data: any, eventName: string) => {
    console.log(`staged ${eventName} with ${data}`);
    if (!triggered) {
      // First event received
      options.eventTriggered?.(data, eventName);
      options.eventActioned?.(data, eventName);
      startProcess();
    } else {
      // Subsequent events
      if (executing) restart = true; // If main event is running, queue restart
      actionCount++;
      actioned = new Date();
      if (executing)
        eventActionQueue.push({ data, eventName }); // Queue event if busy
      else options.eventActioned?.(data, eventName); // Otherwise, call actioned immediately
      console.log(
        `Triggered Event '${options.title ?? 'no name'}' actioned ${actionCount} since ${triggered.toLocaleTimeString()}, at ${actioned.toLocaleTimeString()}`
      );
    }
  };

  /**
   * Starts the staged execution timer and state
   */
  function startProcess() {
    executing = false;
    restart = false;
    options.cancelToken?.reset();
    triggered = new Date();
    actionCount++;
    actioned = triggered;
    timer = setTimeout(intervalEvent, intervalms);
    console.log(`Triggered Event '${options.title ?? 'no name'}' started ${triggered.toLocaleTimeString()}`);
  }
}
export interface WMEventSourceClientInitializer {
  /**
   * callback to indicate online offline status
   * @param value state of the instance
   * @returns
   */
  onlineCallback: (value: boolean) => void;
  afterOnlineEvent: (() => Promise<void>) | (() => void);
  url: string;
}

(function () {
  globalState().wmEventSourceClients = globalState().wmEventSourceClients ?? {};
})();

const _all_ = '__all__';
export class WMEventSourceClient {
  private _initializer: WMEventSourceClientInitializer;
  private static _instances(): { [key: string]: WMEventSourceClient } {
    return globalState().wmEventSourceClients as { [key: string]: WMEventSourceClient };
  }
  userId: string = '00000000-0000-0000-0000-000000000000';
  public static getInstance(
    instanceName?: string,
    factoryOptions?: WMEventSourceClientInitializer
  ): WMEventSourceClient {
    if (!WMEventSourceClient._instances()[instanceName ?? 'default']) {
      if (!factoryOptions) throw new Error(`WMEventSourceClient.getInstance requires factoryOptions on first call`);
      WMEventSourceClient._instances()[instanceName ?? 'default'] = new WMEventSourceClient(factoryOptions);
    }
    return WMEventSourceClient._instances()[instanceName ?? 'default'];
  }
  private _source?: EventSource;
  private _events: { [key: string]: EventSourceHandler[] } = {};
  constructor(factoryOptions: WMEventSourceClientInitializer) {
    this._initializer = factoryOptions;
    this.addEventListener('ping', () => this._ping());
  }

  reconnectFrequencySeconds = 1;

  private _reconnect = debounce(
    () => {
      this.connectUser();
      // Double every attempt to avoid overwhelming server
      this.reconnectFrequencySeconds *= 2;
      // Max out at ~1 minute as a compromise between user experience and server load
      if (this.reconnectFrequencySeconds >= 64) {
        this.reconnectFrequencySeconds = 64;
      }
    },
    () => this.reconnectFrequencySeconds * 1000
  );

  public static pushEventToDefault(eventName: string, data: any) {
    WMEventSourceClient.getInstance().pushEvent(eventName, data);
  }
  public pushEvent(eventName: string, data: any) {
    this._events[eventName]?.forEach(event => {
      event(data, eventName);
    });
    this._events[_all_]?.forEach(event => {
      event(data, eventName);
    });
  }
  connectUser() {
    this.disconnect();

    const user = getCurrentUser();
    if (!user) throw new Error('User not connected');
    const _token = getApiToken();
    const token = encodeURIComponent(_token);
    const sourceUrl = `${this._initializer.url}/api/system/sse?&token=${token}`;
    this._source = new EventSource(sourceUrl);
    this._source.onopen = () => {
      this._initializer.onlineCallback(true);
      this.reconnectFrequencySeconds;
      this._initializer.afterOnlineEvent?.();
    };
    this._source.onmessage = event => {
      const data = JSON.parse(event.data);

      const eventName = data.eventName;
      this._events[eventName]?.forEach(event1 => {
        event1(data, eventName);
      });
      this._events[_all_]?.forEach(event1 => {
        event1(data, eventName);
      });
    };
    this._source.onerror = _err => {
      console.log(_err);
      this._initializer.onlineCallback(false);
      this._source?.close();
      this._source = undefined;
      this._reconnect();
    };
  }

  private _ping = () => {
    console.log('SourceEvent Server Ping');
  };
  disconnect() {
    this._source?.close();
    this._source = undefined;
  }
  addEventListener(eventNames: string | string[], event: EventSourceHandler) {
    const events = Array.isArray(eventNames) ? eventNames : [eventNames];

    events.forEach(_eventName => {
      const eventName = _eventName === '' ? _all_ : _eventName;

      let eventArray: EventSourceHandler[] = this._events[eventName];
      if (!eventArray) {
        eventArray = [];
        this._events[eventName] = eventArray;
      }
      if (!eventArray.find(x => x === event)) eventArray.push(event);
    });
  }
  removeEventListener(eventNames: string | string[], event: EventSourceHandler) {
    const events = Array.isArray(eventNames) ? eventNames : [eventNames];
    events.forEach(_eventName => {
      const eventName = _eventName === '' ? _all_ : _eventName;
      const eventArray: EventSourceHandler[] = this._events[eventName];
      this._events[eventName] = eventArray?.filter(binder => !(binder === event));
    });
  }
}
